<!--
  @component StagePipeline — horizontal ordered step tracker.
  tier:      primitive (visual atom)
  purpose:   render the DeployRun stage machine, the ReleaseLifecycle
             phase machine, the r-arc W3-ordered restoreToPoint
             pipeline, or any other ordered-step view from one shape.
-->
<script lang="ts" module>
  export interface PipelineStage {
    readonly id: string;
    readonly label: string;
  }
</script>

<script lang="ts">
  interface Props {
    /** Ordered stages, left to right. */
    stages: readonly PipelineStage[];
    /** Currently active stage id. */
    current: string;
    /** Stage that failed (if any). Steps before it render as
     *  completed; the failed stage itself renders crossed. */
    failed?: string;
    /** Explicit completed-set override; default = everything
     *  before `current` (or before `failed`). */
    completed?: readonly string[];
  }
  const { stages, current, failed, completed }: Props = $props();

  type StageState = "todo" | "current" | "done" | "failed";

  function stateOf(id: string, idx: number): StageState {
    if (completed?.includes(id)) return "done";
    if (failed === id) return "failed";
    if (current === id) return "current";
    const failedIdx = failed
      ? stages.findIndex((s) => s.id === failed)
      : -1;
    const currentIdx = stages.findIndex((s) => s.id === current);
    const pivot = failedIdx >= 0 ? failedIdx : currentIdx;
    if (pivot >= 0 && idx < pivot) return "done";
    return "todo";
  }
</script>

<ol class="pipeline" aria-label="Pipeline progress">
  {#each stages as s, i (s.id)}
    {@const st = stateOf(s.id, i)}
    <li class="stage" data-state={st}>
      <span class="dot" aria-hidden="true">
        {#if st === "done"}✓{:else if st === "failed"}✗{:else}{i + 1}{/if}
      </span>
      <span class="label">{s.label}</span>
      {#if i < stages.length - 1}
        <span class="connector" data-state={st} aria-hidden="true"></span>
      {/if}
    </li>
  {/each}
</ol>

<style>
  .pipeline {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    align-items: center;
    gap: 0;
    font-family: var(--type-font);
    font-size: 12px;
  }
  .stage {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    color: var(--text-tertiary);
  }
  .stage[data-state="done"] {
    color: var(--success);
  }
  .stage[data-state="current"] {
    color: var(--accent-text);
  }
  .stage[data-state="failed"] {
    color: var(--error);
  }
  .dot {
    width: 22px;
    height: 22px;
    border-radius: var(--radius-pill);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 600;
    background: var(--glass-fill);
    border: 0.5px solid var(--border);
  }
  .stage[data-state="done"] .dot {
    background: var(--success-subtle);
    border-color: var(--success-subtle);
  }
  .stage[data-state="current"] .dot {
    background: var(--accent-subtle);
    border-color: var(--accent-border);
    box-shadow: 0 0 0 3px var(--accent-glow);
  }
  .stage[data-state="failed"] .dot {
    background: var(--error-subtle);
    border-color: var(--error-subtle);
  }
  .label {
    white-space: nowrap;
  }
  .connector {
    display: inline-block;
    height: 1px;
    width: 28px;
    margin: 0 var(--space-3);
    background: var(--border);
  }
  .connector[data-state="done"] {
    background: var(--success);
    opacity: 0.6;
  }
</style>
